import { useState } from 'react';
import { JiraPlanGeneratorService, type JiraEpic } from '../../services/jiraPlanGeneratorService';
import { logger } from '../../services/loggingService';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface JiraPlanViewerProps {
  projectId: string;
  userId: string;
  blueprintId: string;
  jiraProjectKey: string;
}

export function JiraPlanViewer({ projectId, userId, blueprintId, jiraProjectKey }: JiraPlanViewerProps) {
  const [epics, setEpics] = useState<JiraEpic[]>([]);
  const [_loading, _setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [expandedEpic, setExpandedEpic] = useState<string | null>(null);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      
      // Validate blueprint is locked before generating Jira plan
      const { data: blueprint, error: blueprintError } = await supabase
        .from('project_blueprints')
        .select('is_locked')
        .eq('id', blueprintId)
        .maybeSingle();
      
      if (blueprintError) {
        logger.error('Error checking blueprint status', { error: blueprintError, blueprintId });
        throw new Error('Failed to verify blueprint status');
      }
      
      if (!blueprint) {
        throw new Error('Blueprint not found');
      }
      
      if (!blueprint.is_locked) {
        toast.error('Blueprint must be locked before generating Jira plan');
        return;
      }
      
      const generatedEpics = await JiraPlanGeneratorService.generateFromBlueprint(
        userId,
        projectId,
        blueprintId,
        jiraProjectKey
      );
      setEpics(generatedEpics);
      toast.success('Jira plan generated successfully!');
    } catch (error) {
      logger.error('Failed to generate Jira plan', { error, blueprintId, projectId, jiraProjectKey });
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate Jira plan';
      toast.error(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const toggleEpic = (epicKey: string) => {
    setExpandedEpic(expandedEpic === epicKey ? null : epicKey);
  };

  const getTotalStoryPoints = (epic: JiraEpic) => {
    return epic.stories.reduce((sum, story) => sum + (story.storyPoints || 0), 0);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-surface rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text">
              Jira Plan
            </h1>
            <p className="text-text-secondary mt-2">
              Project: {jiraProjectKey}
            </p>
          </div>
          {epics.length === 0 && (
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="btn btn-primary disabled:opacity-50"
            >
              {generating ? 'Generating...' : 'Generate Plan'}
            </button>
          )}
        </div>
      </div>

      {/* Epics List */}
      {epics.length > 0 && (
        <div className="space-y-4">
          {epics.map((epic, epicIndex) => (
            <div key={epicIndex} className="bg-surface rounded-lg shadow-lg overflow-hidden">
              {/* Epic Header */}
              <div
                className="p-6 cursor-pointer hover:bg-background-secondary"
                onClick={() => toggleEpic(epic.title)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="badge badge-secondary text-sm font-semibold">
                        EPIC-{epicIndex + 1}
                      </span>
                      <h3 className="text-xl font-bold text-text">
                        {epic.title}
                      </h3>
                    </div>
                    <p className="text-text-secondary">
                      {epic.description}
                    </p>
                    <div className="flex items-center gap-4 mt-3">
                      <span className="text-sm text-text-secondary">
                        {epic.stories.length} stories
                      </span>
                      <span className="text-sm text-text-secondary">
                        {getTotalStoryPoints(epic)} story points
                      </span>
                    </div>
                  </div>
                  <svg
                    className={`w-6 h-6 text-text-tertiary transition-transform ${
                      expandedEpic === epic.title ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Stories */}
              {expandedEpic === epic.title && (
                <div className="border-t border-border bg-background-secondary p-6">
                  <div className="space-y-4">
                    {epic.stories.map((story, storyIndex) => (
                      <div key={storyIndex} className="bg-surface rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="badge badge-primary text-xs font-semibold">
                                STORY-{storyIndex + 1}
                              </span>
                              <h4 className="font-semibold text-text">
                                {story.title}
                              </h4>
                            </div>
                            <p className="text-sm text-text-secondary mb-3">
                              {story.description}
                            </p>
                          </div>
                          <span className="badge-success text-sm font-semibold ml-4">
                            {story.storyPoints} pts
                          </span>
                        </div>

                        {/* Acceptance Criteria */}
                        <div className="mb-3">
                          <h5 className="text-sm font-semibold text-text mb-2">
                            Acceptance Criteria:
                          </h5>
                          <ul className="space-y-1">
                            {story.acceptanceCriteria.map((criteria, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                                <svg className="w-4 h-4 text-success mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                {criteria}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Tasks */}
                        {story.tasks && story.tasks.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold text-text mb-2">
                              Tasks:
                            </h5>
                            <ul className="space-y-1">
                              {story.tasks.map((task, idx) => typeof task === 'string' ? (
                                <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                                  <svg className="w-4 h-4 text-text-tertiary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                  </svg>
                                  {task}
                                </li>
                              ) : (
                                <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                                  <svg className="w-4 h-4 text-text-tertiary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                  </svg>
                                  {task.title}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!_loading && !generating && epics.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12 text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No Jira Plan Yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Generate a Jira plan from your frozen blueprint to create epics and stories.
          </p>
        </div>
      )}
    </div>
  );
}
